import { TabMenu, TabMenuList, TabMenuTrigger, Tag } from '@ads/components-react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface PersonFormTabsStep {
  readonly key: string;
  readonly title: string;
}

export interface PersonFormTabsProps {
  /** Lista de etapas a exibir como tabs */
  readonly steps: ReadonlyArray<PersonFormTabsStep>;
  /** Índice da etapa atualmente ativa (0-based) */
  readonly currentStep: number;
  /** Número de erros de validação por chave de etapa */
  readonly errorCountByStep: Record<string, number>;
  /** Indica se o usuário já submeteu o formulário (controla exibição de erros) */
  readonly isSubmitted: boolean;
  /** Callback chamado quando o usuário clica em uma etapa */
  readonly onStepClick: (index: number) => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Navegação por tabs (modo edição) para o formulário de pessoa.
 *
 * Renderiza uma faixa de tabs do ADS em modo controlado. Cada tab corresponde
 * a uma etapa e exibe um badge negativo quando há erros após submissão.
 * Diferente do stepper, não comunica progresso sequencial — todas as seções
 * têm o mesmo peso visual, adequado a fluxo de edição não-linear.
 */
export function PersonFormTabs({
  steps,
  currentStep,
  errorCountByStep,
  isSubmitted,
  onStepClick,
}: PersonFormTabsProps) {
  const activeKey = steps[currentStep]?.key ?? steps[0]?.key ?? '';

  return (
    <div className="overflow-x-auto rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface">
      <TabMenu
        className="w-full"
        value={activeKey}
        onValueChange={next => {
          const index = steps.findIndex(s => s.key === next);
          if (index >= 0) onStepClick(index);
        }}
      >
        <TabMenuList className="w-full">
          {steps.map(step => {
            const errorCount = errorCountByStep[step.key] ?? 0;
            const hasError = isSubmitted && errorCount > 0;
            return (
              <TabMenuTrigger className="w-full" key={step.key} value={step.key}>
                <span className="flex items-center gap-quark">
                  <span>{step.title}</span>
                  {hasError && (
                    <Tag
                      size="sm"
                      status="negative"
                      label={String(errorCount)}
                      custom={{ text: 'text-nowrap' }}
                    />
                  )}
                </span>
              </TabMenuTrigger>
            );
          })}
        </TabMenuList>
      </TabMenu>
    </div>
  );
}
